import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsageSummary } from 'data/reducers/subscriptionReducer';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import Spinner from 'components/Spinner';
import { CircularProgress } from '../CircularProgress';
import { useUpgradeIncentive } from 'hooks/useUpgradeIncentive';
import moment from 'moment';

export const SubscriptionUsageSummary = () => {
	const dispatch = useDispatch();
	const { showUpgradePlanIncentive } = useUpgradeIncentive();

	// Get active organization
	const { active: activeOrganization } = useOrganizations();

	const { usageSummary, isLoadingUsageSummary, usageSummaryError } =
		useSelector((state) => state.subscription);

	useEffect(() => {
		dispatch(fetchUsageSummary());
	}, [dispatch]);

	const planLimits = useMemo(() => usageSummary?.limits || {}, [usageSummary]);

	// Organization context display
	const organizationName = useMemo(() => {
		return activeOrganization?.name || 'Personal';
	}, [activeOrganization]);

	if (isLoadingUsageSummary) {
		return (
			<div className="tw-flex tw-justify-center tw-py-12">
				<Spinner />
			</div>
		);
	}

	if (usageSummaryError) {
		return (
			<div className="violerts-v3-root tw-rounded-[8px] tw-border tw-border-border-01 tw-bg-surface-layer-01 tw-p-6 tw-text-sm tw-text-icon-error">
				Error loading subscription usage: {usageSummaryError}
			</div>
		);
	}

	if (!usageSummary) {
		return null;
	}

	const { usage } = usageSummary;

	return (
		<div className="violerts-v3-root tw-flex tw-flex-col tw-gap-2">
			{/* Figma frame 31:662: title+date row, then the org banner */}
			<div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-2">
				<h2 className="tw-text-lg tw-font-bold tw-text-text-primary">Subscription Usage</h2>
				<span className="tw-text-[11px] tw-text-text-secondary">
					as of {moment().format('MM/DD/YYYY')}
				</span>
			</div>
			{activeOrganization && (
				<div className="tw-flex tw-items-center tw-gap-2 tw-rounded-[4px] tw-border-l-4 tw-border-[#007BFF] tw-bg-button-informative tw-px-4 tw-py-2">
					<span className="tw-text-xs tw-font-bold tw-text-text-primary">{organizationName}</span>
					<span className="tw-text-xs tw-text-text-primary">•</span>
					<span className="tw-text-xs tw-text-text-primary">Organization Usage</span>
				</div>
			)}
			<div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4 lg:tw-flex-row">
				<div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-rounded-[8px] tw-border tw-border-border-01 tw-bg-surface-layer-01 tw-p-6">
					<CircularProgress
						className="horizontal"
						title={`Property Usage (${usage?.properties || 0}/${
							planLimits?.properties || 30
						})`}
						used={usage?.properties || 0}
						total={planLimits?.properties || 30}
						size={160}
						strokeWidth={12}
						withUpgradePlanIncentive={true}
						onUpgradeClick={() => showUpgradePlanIncentive({})}
					/>
				</div>
			</div>
		</div>
	);
};
